import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import BulkReferralUpload from './BulkReferralUpload';

const CreateReferralModal = ({ isOpen, onClose, onSuccess }) => {
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'bulk'
  const [defaultProcedureType, setDefaultProcedureType] = useState('');

  // Modal minimize state
  const [isMinimized, setIsMinimized] = useState(false);
  const [bulkUploadProcessingStatus, setBulkUploadProcessingStatus] = useState(null);

  const [formData, setFormData] = useState({
    patient_id: '',
    health_system_id: '',
    assigned_to_id: '',
    case_name: '',
    case_description: '',
    overall_deadline: '',
    urgency_level: 'medium',
  });

  const [patients, setPatients] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [healthSystemUsers, setHealthSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPatient, setSearchPatient] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Patient creation states
  const [showCreatePatientForm, setShowCreatePatientForm] = useState(false);
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState(null); // Track if we're editing an existing patient
  const [newPatientData, setNewPatientData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    phone: '',
    email: '',
    patient_id: '',
  });

  // Fetch health systems
  useEffect(() => {
    const fetchData = async () => {
      try {
        const healthSystemsResponse = await adminApi.get('admin/health-systems/');

        const healthSystemsData = healthSystemsResponse.data?.health_systems || [];
        setHealthSystems(healthSystemsData);

        // Auto-select if only one health system
        if (healthSystemsData.length === 1) {
          setFormData(prev => ({ ...prev, health_system_id: healthSystemsData[0].id }));
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Fetch health system users when health_system_id changes
  useEffect(() => {
    const fetchHealthSystemUsers = async () => {
      if (!formData.health_system_id) {
        setHealthSystemUsers([]);
        return;
      }

      try {
        const response = await adminApi.get(`admin/health-system/${formData.health_system_id}/users/`);
        const users = response.data?.users || [];
        setHealthSystemUsers(users);
      } catch (err) {
        console.error('Failed to fetch health system users:', err);
        setHealthSystemUsers([]);
      }
    };

    fetchHealthSystemUsers();
  }, [formData.health_system_id]);

  // Set default deadline to 1 week from today when modal opens
  useEffect(() => {
    if (isOpen && !formData.overall_deadline) {
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
      const formattedDate = oneWeekFromNow.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
      setFormData(prev => ({ ...prev, overall_deadline: formattedDate }));
    }
  }, [isOpen, formData.overall_deadline]);

  // Search patients
  const handleSearchPatients = async (query) => {
    if (!query || query.length < 2) {
      setPatients([]);
      return;
    }

    try {
      const params = {
        search: query,
        page_size: 20
      };

      // Add health system ID for scoped search
      if (formData.health_system_id) {
        params.health_system_id = formData.health_system_id;
      }

      const response = await adminApi.get('admin/patient/', { params });

      if (response.data?.patients) {
        setPatients(response.data.patients);
      } else {
        setPatients([]);
      }
    } catch (err) {
      console.error('Failed to search patients:', err);
      setError(`Failed to search patients: ${err.message}`);
    }
  };

  // Handle patient creation or update
  const handleCreatePatient = async () => {
    setCreatingPatient(true);
    setError(null);

    try {
      // Validate required fields
      if (!newPatientData.first_name || !newPatientData.last_name || !newPatientData.dob) {
        setError('Please fill in all required fields (First Name, Last Name, Date of Birth)');
        setCreatingPatient(false);
        return;
      }

      // Health system is required for patient creation (but not for update)
      if (!editingPatientId && !formData.health_system_id) {
        setError('Please select a health system before creating a patient');
        setCreatingPatient(false);
        return;
      }

      const payload = {
        first_name: newPatientData.first_name,
        last_name: newPatientData.last_name,
        dob: newPatientData.dob,
        phone: newPatientData.phone || undefined,
        email: newPatientData.email || undefined,
        patient_id: newPatientData.patient_id || undefined,
      };

      // Only include health_system_id when creating (not updating)
      if (!editingPatientId) {
        payload.health_system_id = formData.health_system_id;
      }

      let response;
      if (editingPatientId) {
        // Update existing patient
        response = await adminApi.patch(`admin/patients/${editingPatientId}/`, payload);
      } else {
        // Create new patient
        response = await adminApi.post('admin/patients/', payload);
      }

      if (response.data?.patient) {
        const updatedPatient = response.data.patient;

        // Auto-select the patient
        setFormData(prev => ({ ...prev, patient_id: updatedPatient.id }));
        setSearchPatient(`${updatedPatient.first_name} ${updatedPatient.last_name}`);
        setSelectedPatient(updatedPatient);

        // Close the form and reset
        setShowCreatePatientForm(false);
        setEditingPatientId(null);
        setNewPatientData({
          first_name: '',
          last_name: '',
          dob: '',
          phone: '',
          email: '',
          patient_id: '',
        });
      }
    } catch (err) {
      console.error(`Failed to ${editingPatientId ? 'update' : 'create'} patient:`, err);
      setError(err.response?.data?.error || err.message || `Failed to ${editingPatientId ? 'update' : 'create'} patient`);
    } finally {
      setCreatingPatient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Clean up payload - remove empty assigned_to_id
      const payload = { ...formData };
      if (!payload.assigned_to_id) {
        delete payload.assigned_to_id;
      }

      const response = await adminApi.post('admin/referrals/', payload);

      if (response.data?.referral) {
        onSuccess(response.data.referral);
        onClose();
        resetForm();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create referral');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      health_system_id: '',
      assigned_to_id: '',
      case_name: '',
      case_description: '',
      overall_deadline: '',
      urgency_level: 'medium',
    });
    setPatients([]);
    setSearchPatient('');
    setSelectedPatient(null);
    setError(null);
    setShowCreatePatientForm(false);
    setEditingPatientId(null);
    setNewPatientData({
      first_name: '',
      last_name: '',
      dob: '',
      phone: '',
      email: '',
      patient_id: '',
    });
    setUploadMode('single');
    setDefaultProcedureType('');
    setIsMinimized(false);
    setBulkUploadProcessingStatus(null);
  };

  const handleBulkUploadSuccess = (results) => {
    // For bulk upload, we don't call onSuccess since it expects a single referral object
    // Instead, we'll just close the modal and the parent component can handle the refresh

    // Auto-close modal after successful bulk upload if all rows succeeded
    if (results.failed_count === 0) {
      setTimeout(() => {
        onClose();
        resetForm();
        // Trigger a page reload or parent refresh if needed
        if (onSuccess) {
          window.location.reload();
        }
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Full Modal */}
      <div
        className={`modal show ${!isMinimized ? 'd-block' : ''}`}
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: isMinimized ? 'none' : 'block'
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
              <div className="modal-header d-flex justify-content-between gap-2">
                <h5 className="modal-title">Create New Referral</h5>
                <div className="d-flex align-items-center gap-2">
                  {/* Show minimize button when in bulk mode and processing */}
                  <button
                    type="button"
                    className="btn btn-link text-secondary p-0"
                    onClick={() => setIsMinimized(true)}
                    title="Minimize"
                    style={{ fontSize: '1.2rem', lineHeight: 1 }}
                  >
                    <i className="fas fa-window-minimize"></i>
                  </button>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                  ></button>
                </div>
              </div>

          <div className="modal-body">
            {/* Mode Selector */}
            <div className="mb-4">
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="uploadMode"
                  id="mode-single"
                  checked={uploadMode === 'single'}
                  onChange={() => setUploadMode('single')}
                />
                <label className="btn btn-outline-primary" htmlFor="mode-single">
                  <i className="fas fa-user me-2"></i>
                  Single Referral
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="uploadMode"
                  id="mode-bulk"
                  checked={uploadMode === 'bulk'}
                  onChange={() => setUploadMode('bulk')}
                />
                <label className="btn btn-outline-primary" htmlFor="mode-bulk">
                  <i className="fas fa-file-csv me-2"></i>
                  Bulk Upload (CSV)
                </label>
              </div>
            </div>

            {error && uploadMode === 'single' && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}

            {uploadMode === 'single' && (
              <form onSubmit={handleSubmit}>
            {/* Health System - Only show if multiple health systems */}
            {healthSystems.length > 1 && (
              <div className="mb-3">
                <label className="form-label">
                  Health System <span className="text-danger">*</span>
                </label>
                <select
                  value={formData.health_system_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, health_system_id: e.target.value }))}
                  className="form-select"
                  required
                >
                  <option value="">Select Health System</option>
                  {healthSystems.map((hs) => (
                    <option key={hs.id} value={hs.id}>
                      {hs.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Patient Search - Only enabled after health system selected */}
            <div className="mb-3">
              <label className="form-label">
                Patient <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                placeholder={
                  formData.health_system_id
                    ? "Search patient by name or create new..."
                    : "Select a health system first"
                }
                value={searchPatient}
                onChange={(e) => {
                  setSearchPatient(e.target.value);
                  handleSearchPatients(e.target.value);
                }}
                className="form-control"
                required={!formData.patient_id}
                disabled={!formData.health_system_id}
              />
              {!formData.health_system_id && (
                <small className="text-muted">Please select a health system above to search for patients</small>
              )}

              {/* No patients found - show Create Patient option */}
              {searchPatient && searchPatient.length >= 2 && patients.length === 0 && !showCreatePatientForm && !formData.patient_id && (
                <div className="mt-2">
                  <div className="alert alert-warning small mb-2">
                    No patients found matching &quot;{searchPatient}&quot;
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => setShowCreatePatientForm(true)}
                  >
                    Create New Patient
                  </button>
                </div>
              )}

              {/* Inline Patient Creation Form */}
              {showCreatePatientForm && (
                <div className="mt-3 p-3 border rounded bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      {editingPatientId ? 'Update Patient' : 'Create New Patient'}
                    </h6>
                    <button
                      type="button"
                      className="btn-close btn-sm"
                      onClick={() => {
                        setShowCreatePatientForm(false);
                        setEditingPatientId(null);
                        setNewPatientData({
                          first_name: '',
                          last_name: '',
                          dob: '',
                          phone: '',
                          email: '',
                          patient_id: '',
                        });
                      }}
                    ></button>
                  </div>

                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newPatientData.first_name}
                        onChange={(e) => setNewPatientData({ ...newPatientData, first_name: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newPatientData.last_name}
                        onChange={(e) => setNewPatientData({ ...newPatientData, last_name: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={newPatientData.dob}
                        onChange={(e) => setNewPatientData({ ...newPatientData, dob: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Phone</label>
                      <input
                        type="tel"
                        className="form-control form-control-sm"
                        value={newPatientData.phone}
                        onChange={(e) => setNewPatientData({ ...newPatientData, phone: e.target.value })}
                        placeholder="+1-XXX-XXX-XXXX"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={newPatientData.email}
                        onChange={(e) => setNewPatientData({ ...newPatientData, email: e.target.value })}
                        placeholder="john.doe@example.com"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">MRN / Patient ID</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newPatientData.patient_id}
                        onChange={(e) => setNewPatientData({ ...newPatientData, patient_id: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={handleCreatePatient}
                      disabled={creatingPatient || !newPatientData.first_name || !newPatientData.last_name || !newPatientData.dob}
                    >
                      {creatingPatient ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          {editingPatientId ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          {editingPatientId ? 'Update Patient' : 'Create Patient'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setShowCreatePatientForm(false);
                        setEditingPatientId(null);
                        setNewPatientData({
                          first_name: '',
                          last_name: '',
                          dob: '',
                          phone: '',
                          email: '',
                          patient_id: '',
                        });
                      }}
                      disabled={creatingPatient}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {patients.length > 0 && (
                <div className="mt-2 border rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {patients.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, patient_id: patient.id }));
                        setSearchPatient(`${patient.first_name} ${patient.last_name}`);
                        setSelectedPatient(patient);
                        setPatients([]);
                      }}
                      className="w-100 btn btn-light text-start border-bottom"
                    >
                      <div className="fw-semibold">{patient.first_name} {patient.last_name}</div>
                      <small className="text-muted">
                        DOB: {patient.dob ? new Date(patient.dob).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'N/A'} |
                        MRN: {patient.patient_id || 'N/A'}
                      </small>
                    </button>
                  ))}
                </div>
              )}

              {/* Show selected patient info */}
              {formData.patient_id && selectedPatient && (
                <div className="mt-2 alert alert-success mb-0">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold">
                        <i className="fas fa-check-circle me-2"></i>
                        {selectedPatient.first_name} {selectedPatient.last_name}
                      </div>
                      <small className="text-muted d-block mt-1">
                        DOB: {selectedPatient.dob ? new Date(selectedPatient.dob).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'N/A'} |
                        MRN: {selectedPatient.patient_id || 'N/A'}
                        {selectedPatient.phone && ` | Phone: ${selectedPatient.phone}`}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm text-black btn-outline-secondary"
                        onClick={() => {
                          // Pre-fill the patient creation form with existing patient data
                          const patientData = {
                            first_name: selectedPatient.first_name || '',
                            last_name: selectedPatient.last_name || '',
                            dob: selectedPatient.dob || '',
                            phone: selectedPatient.phone || '',
                            email: selectedPatient.email || '',
                            patient_id: selectedPatient.patient_id || '',
                          };
                          setNewPatientData(patientData);
                          setEditingPatientId(selectedPatient.id); // Set the ID of the patient being edited
                          setShowCreatePatientForm(true);
                          setFormData(prev => ({ ...prev, patient_id: '' }));
                          setSelectedPatient(null);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, patient_id: '' }));
                          setSelectedPatient(null);
                          setSearchPatient('');
                          setPatients([]);
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Assigned To */}
            <div className="mb-3">
              <label className="form-label">
                Assigned To
              </label>
              <select
                value={formData.assigned_to_id}
                onChange={(e) => setFormData(prev => ({ ...prev, assigned_to_id: e.target.value }))}
                className="form-select"
                disabled={!formData.health_system_id}
              >
                <option value="">Unassigned</option>
                {healthSystemUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
              {!formData.health_system_id && (
                <small className="text-muted">Select a health system to see available users</small>
              )}
            </div>

            {/* Case Name */}
            <div className="mb-3">
              <label className="form-label">
                Case/Encounter Type
              </label>
              <input
                type="text"
                value={formData.case_name}
                onChange={(e) => setFormData(prev => ({ ...prev, case_name: e.target.value }))}
                placeholder="e.g., Pre-op Clearances for Surgery"
                className="form-control"
              />
            </div>

            {/* Case Description */}
            <div className="mb-3">
              <label className="form-label">
                Description
              </label>
              <textarea
                value={formData.case_description}
                onChange={(e) => setFormData(prev => ({ ...prev, case_description: e.target.value }))}
                placeholder="Additional case/encounter details..."
                rows={3}
                className="form-control"
              />
            </div>

            {/* Deadline and Urgency */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">
                  Overall Deadline
                </label>
                <input
                  type="datetime-local"
                  value={formData.overall_deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, overall_deadline: e.target.value }))}
                  className="form-control"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Urgency Level
                </label>
                <select
                  value={formData.urgency_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, urgency_level: e.target.value }))}
                  className="form-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            {/* Form Actions */}
            <div className="d-flex gap-2 pt-3 mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading ||
                  !formData.patient_id ||
                  !formData.health_system_id
                }
                title={
                  !formData.patient_id ? 'Please select a patient' :
                  !formData.health_system_id ? 'Please select a health system' :
                  'Create referral'
                }
              >
                {loading ? 'Creating...' : 'Create Referral'}
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
              </form>
            )}

            {/* Bulk Upload Mode */}
            {uploadMode === 'bulk' && (
              <div>
              {/* Health System Selector for Bulk Upload */}
              {healthSystems.length > 1 && (
                <div className="mb-3">
                  <label className="form-label">
                    Health System <span className="text-danger">*</span>
                  </label>
                  <select
                    value={formData.health_system_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, health_system_id: e.target.value }))}
                    className="form-select"
                    required
                  >
                    <option value="">Select Health System</option>
                    {healthSystems.map((hs) => (
                      <option key={hs.id} value={hs.id}>
                        {hs.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    All referrals will be assigned to this health system
                  </small>
                </div>
              )}

              {healthSystems.length === 1 && (
                <div className="alert alert-info mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  All referrals will be created for: <strong>{healthSystems[0].name}</strong>
                </div>
              )}

              {/* Default Procedure Type */}
              <div className="mb-4">
                <label className="form-label">
                  Default Case/Encounter Type
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., Colonoscopy Clearance, Pre-op Evaluation"
                  value={defaultProcedureType}
                  onChange={(e) => setDefaultProcedureType(e.target.value)}
                />
                <small className="text-muted">
                  Used as default case/encounter name for rows where case_name is not specified
                </small>
              </div>

              {/* Bulk Upload Component */}
              <BulkReferralUpload
                healthSystemId={formData.health_system_id}
                defaultProcedureType={defaultProcedureType}
                onSuccess={handleBulkUploadSuccess}
                onProcessingStatusChange={setBulkUploadProcessingStatus}
              />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Minimized Widget */}
      {isMinimized && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '400px',
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1050,
          padding: '16px'
        }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>
              {uploadMode === 'bulk' ? 'Bulk Upload Processing' : 'Creating Referral'}
            </strong>
            <div>
              <button
                className="btn btn-sm btn-link p-0 me-2"
                onClick={() => setIsMinimized(false)}
                title="Expand"
              >
                <i className="fas fa-expand"></i>
              </button>
              <button
                className="btn btn-sm btn-link p-0 text-danger"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          {/* Bulk upload progress */}
          {uploadMode === 'bulk' && bulkUploadProcessingStatus && (
            <>
              <div className="progress mb-2" style={{ height: '8px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${bulkUploadProcessingStatus.progress || 0}%` }}
                ></div>
              </div>

              <div className="small text-muted mb-1">
                {bulkUploadProcessingStatus.message}
              </div>

              {bulkUploadProcessingStatus.fallback_message && (
                <div className="alert alert-warning small mb-2 p-2" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  {bulkUploadProcessingStatus.fallback_message}
                </div>
              )}

              {bulkUploadProcessingStatus.current_row && bulkUploadProcessingStatus.total_rows && (
                <div className="small text-muted">
                  {bulkUploadProcessingStatus.current_row} / {bulkUploadProcessingStatus.total_rows} rows
                </div>
              )}

              {bulkUploadProcessingStatus.status === 'completed' && (
                <div className="text-success small mt-2">
                  <i className="fas fa-check-circle me-1"></i>
                  Click to view results
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CreateReferralModal;
